import React, { useContext, useEffect, useRef, useState } from 'react';
import { addClasses, mergeClasses, cn } from '#lib/helpers/class.js';
import { defaultMakeReactKey, translateLanguages } from '#helpers/helpers.js';
import select from '#lib/components/select.jsx';
import Input from '#lib/components/input.jsx';
import { Icon } from '@iconify-icon/react';
import { SubSectionHeading } from '#components/headings.jsx';
import FeedbackModal from './feedback-modal.jsx';
import TasksModal from './tasks-modal.jsx';
import MailListModal from './mail-list-modal.jsx';
import Combobox from '#lib/components/combobox';
import { CustomTextArea } from '#components/custom-text-area.jsx';
import useUserQueries from '#hooks/use-user-query.js';
import useDebounce from '#lib/hooks/use-debounce.js';
import Button from '#lib/components/button.jsx';
import PendingUpdatesModal from './pending-updates.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { DiffReport } from './diff/diff-report.jsx';
import toast from 'react-hot-toast';
import { useManageDate } from '#hooks/use-manage-date.js';
import TranslationPanel from './translation-panel.jsx';
import useTranslation from '#hooks/use-translation.js';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { LoadingSkeleton } from '#lib/components/loading.jsx';
import Portal from '#lib/components/portal.jsx';
import Checkbox from '#lib/components/checkbox.jsx';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import useSupportedLanguages from '#hooks/use-supported-languages.js';
import { ItineraryTag } from '#routes/manage/itinerary/search/components/tag.jsx';
import { AddTagPanel } from '#components/add-tag-panel.jsx';
import { ItineraryContext } from '#routes/manage/itinerary/edit.jsx';
import useNewsletter from './use-newsletter.js';
import useAuth from '#lib/hooks/use-auth.jsx';

export function Header(props) {
    const {
        crud,
        form,
        id,
        supportedLanguages,
        profileProps,
        languageProps,
        handleAssignedToName,
        headerError,
        clearError,
        itineraryOptions,
        diffModel,
        isSaving,
        setIsSaving,
        aiMutation,
        handleGenerateDescription,
        setIsTranslating
    } = props;

    const context = useContext(ItineraryContext);
    const {
        profileLanguage,
        title,
        activeProfile,
        description,
        startDate,
        endDate,
        groupLeader,
        emergencyNumber,
        email,
        phoneNumber,
        assignedTo,
        pnr,
        notificationsEmail,
        connectionId,
        feedbacks,
        setFeedbacks,
        clients,
        setClients,
        tasks,
        setTasks,
        tags,
        associateTag,
        deleteTag,
        documents,
        days,
        plans,
        addDay,
        deleteDay,
        movePlan,
        edit: editPlan,
        intlLang
    } = context;

    const {
        meta: { data: userData }
    } = useAuth();

    const cantModify = !userData.mainUser && form.data.isBlocked;

    const [userQuery, setUserQuery] = useState('');
    const search = useDebounce(userQuery, 300);
    const { usersList, isSuccess } = useUserQueries(search);
    useEffect(() => {
        if (assignedTo) {
            const user = usersList?.find((el) => el.id === assignedTo);
            if (user) {
                const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`;
                setUserQuery(fullName);
                handleAssignedToName(fullName);
            } else setUserQuery('');
        }
    }, [isSuccess]);

    const handleChangeAssignedTo = (value) => {
        if (!value) {
            setUserQuery('');
            form.handleChange('assignedTo', '');
            return;
        }
        form.handleChange('assignedTo', value.id);
        if (value.email) form.handleChange('email', value.email);
        if (value.phoneNumber) form.handleChange('phoneNumber', value.phoneNumber);
        if (!emergencyNumber) form.handleChange('emergencyNumber', value.phoneNumber);
        const fullName = (value.firstName ?? '') + ' ' + (value.lastName ?? '');
        setUserQuery(fullName);
        handleAssignedToName(fullName);
    };

    // Get the taskName from the URL when I come from the dashboard
    const urlParams = new URLSearchParams(window.location?.search);
    const taskName = urlParams.get('taskName');

    const [show, setShow] = useState(false);
    const [showPendingUpdates, setShowPendingUpdates] = useState(false);
    const [showTasks, setShowTasks] = useState(!!taskName);
    const [showFeedbacks, setShowFeedbacks] = useState(false);
    const [showTranslationPanel, setShowTranslationPanel] = useState(false);

    // Translation logic
    const handleShowTranslationPanel = () => {
        setShowTranslationPanel((prev) => !prev);
    };

    const [newLanguage, setNewLanguage] = useState('');
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!isSaving && newLanguage !== '') {
            //TODO: chiama troppe volte la mutation
            const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
            const languageName = languageNames.of(newLanguage);
            handleTranslateItinerary({ id: newLanguage, name: languageName });
            setIsTranslating(true);
        }
    }, [isSaving]);

    // save
    const handleClick = (language) => {
        setNewLanguage(language);
        buttonRef.current.click();
        setIsSaving(true);
    };

    // change language in dropdown
    const handleChangeLanguage = (language) => {
        if (language === 'more') return handleShowTranslationPanel();
        if (!confirm(_t.edit.translation.translationConfirm())) return;
        handleClick(language);
    };

    // mutation
    const { translationMutation } = useTranslation();
    const handleTranslateItinerary = (language) => {
        translationMutation.mutate({ language, id });
    };

    const { data, isLoading } = useSupportedLanguages();
    const accountLanguages = data?.supportedLanguages ?? [];
    const languages = isLoading
        ? translateLanguages([...supportedLanguages], intlLang)
        : translateLanguages([...supportedLanguages, ...accountLanguages], intlLang);
    // Send Manual Notification
    const [isSending, setIsSending] = useState(false);
    const sendNotification = useNewsletter({ setIsSending });
    const handleSendNotification = () => {
        setIsSending(true);
        const div = document.createElement('div');
        const root = createRoot(div);
        flushSync(() => {
            root.render(
                <DiffReport
                    diff={diffModel}
                    options={{
                        itineraryOptions,
                        totalDocs: documents
                    }}
                />
            );
        });
        const html = div.innerHTML;
        if (!clients.length) return toast.error(_t.edit.noneInMailingList());
        sendNotification.mutate({ id, clients, html });
    };

    const handleShowPendingUpdates = () => {
        setShowPendingUpdates(true);
    };

    // Function to add days to a date
    const { handleChangeStartDate, handleChangeEndDate, setOutdatedStartDate, setOutdatedEndDate } = useManageDate({
        startDate,
        endDate,
        days,
        plans,
        addDay,
        deleteDay,
        movePlan,
        editPlan,
        form,
        headerError,
        clearError
    });

    const [addTagPanel, showAddTagPanel] = useState(false);

    return (
        <form className="md:fixed md:w-[275px] pl-3 pt-[55px] col-span-full" {...form.props.form}>
            <div className="w-full overflow-auto md:max-h-[calc(100vh-55px)] pr-2 flex flex-col gap-1 pb-4">
                <input ref={buttonRef} className="hidden" {...form.props.submit}></input>
                <div className="flex flex-col w-full gap-2 pt-2">
                    {crud.data.loading && (
                        <Portal>
                            <LoadingSkeleton
                                className={addClasses(
                                    'fixed w-full h-full top-14 left-0 flex flex-col gap-4 items-center justify-center bg-opacity-75'
                                )}
                                style={{ zIndex: 999 }}
                            >
                                <Icon icon="svg-spinners:blocks-shuffle-3" width="40" height="40" />
                                {isSaving ? _t.edit.saveInProgress() : _t.edit.translation.workInProgress()}
                            </LoadingSkeleton>
                        </Portal>
                    )}
                    <div>
                        <p className="text-primary-600">{_t.crud.itinerary.itinerary()}</p>
                        <CustomTextArea
                            value={title}
                            name="title"
                            placeholder={_t.edit.newItinerary()}
                            label={_t.edit.fields.title()}
                            form={form}
                            className={addClasses('font-bold text-xl')}
                            hasError={headerError?.some((el) => el === 'title')}
                            clearError={clearError}
                            language={profileLanguage}
                            crud={crud}
                        />
                        <CustomTextArea
                            value={description}
                            label={_t.edit.fields.description()}
                            name="description"
                            placeholder={_t.edit.fields.description()}
                            form={form}
                            className={addClasses('text-sm')}
                            language={profileLanguage}
                            crud={crud}
                            onClick={() => handleGenerateDescription(true)}
                            busy={aiMutation.isLoading}
                            isSuccess={aiMutation.isSuccess}
                        />
                    </div>

                    <div>
                        <SubSectionHeading>{_t.edit.date()}</SubSectionHeading>
                        <div className="flex flex-col gap-1 p-2 pt-1 border rounded-b bg-neutral-100 border-neutral-300">
                            <div className="grid grid-cols-2 gap-1 overflow-x-hidden">
                                <div className="col-span-1">
                                    <label htmlFor="startDate" className="text-xs text-neutral-700">
                                        {_t.edit.fields.startDate()}
                                    </label>
                                    <Input.Full
                                        {...form.register('startDate')}
                                        id="startDate"
                                        form={form.props.form.id}
                                        type="date"
                                        noBorder={true}
                                        hasError={headerError?.some((el) => el === 'startDate')}
                                        onFocus={(e) => {
                                            setOutdatedStartDate(startDate);
                                        }}
                                        onBlur={handleChangeStartDate}
                                        inputClassName="py-0 border-b border-neutral-300 rounded-none"
                                        className="border-b rounded-none w-min border-neutral-300"
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label htmlFor="endDate" className="text-xs text-neutral-700">
                                        {_t.edit.fields.endDate()}
                                    </label>
                                    <div className="flex items-baseline w-full">
                                        <Input.Full
                                            {...form.register('endDate')}
                                            id="endDate"
                                            form={form.props.form.id}
                                            type="date"
                                            noBorder={true}
                                            hasError={headerError?.some((el) => el === 'endDate')}
                                            onFocus={(e) => {
                                                setOutdatedEndDate(endDate);
                                            }}
                                            onBlur={handleChangeEndDate}
                                            inputClassName="py-0 border-b border-neutral-300 rounded-none"
                                            className="border-b rounded-none w-min border-neutral-300"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="text-sm italic text-neutral-900">
                                {days.length > 0 ? (
                                    <>
                                        {'( '}
                                        <a href="#outline" className="cursor-pointer text-neutral-900">
                                            {_t.edit.fields.itineraryDuration()} {days.length}{' '}
                                            {days.length === 1 ? _t.edit.day() : _t.edit.days()}
                                        </a>
                                        {' )'}
                                    </>
                                ) : (
                                    <>
                                        <a href="#outline" className="cursor-pointer text-neutral-900">
                                            {_t.edit.addDays()}
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <SubSectionHeading>{_t.edit.headings()}</SubSectionHeading>
                        <div className="flex flex-col gap-1 p-2 pt-1 border rounded-b bg-neutral-100 border-neutral-300">
                            <InputText
                                value={groupLeader || ''}
                                name="groupLeader"
                                placeholder={_t.edit.fields.groupLeader()}
                                form={form}
                                icon={<Icon icon="line-md:person" width="1rem" height="1rem" />}
                            />

                            <InputText
                                value={pnr || ''}
                                placeholder={_t.edit.pnr()}
                                form={form}
                                name="pnr"
                                icon={<Icon icon="material-symbols:numbers-rounded" width="1rem" height="1rem" />}
                            />
                            <InputText
                                value={notificationsEmail || ''}
                                placeholder={_t.edit.fields.notificationsEmail()}
                                form={form}
                                name="notificationsEmail"
                                type="email"
                                icon={<Icon icon="material-symbols:numbers-rounded" width="1rem" height="1rem" />}
                            />
                        </div>
                    </div>

                    <div>
                        <SubSectionHeading>{_t.edit.layout()}</SubSectionHeading>
                        <div className="flex flex-col gap-1 p-2 pt-1 border rounded-b bg-neutral-100 border-neutral-300">
                            <div className="flex w-full mt-1 gap-x-1">
                                <Checkbox.Full
                                    className="flex gap-2 w-fit"
                                    label={_t.edit.planner.blockItinerary()}
                                    {...form.registerCheckbox('isBlocked')}
                                    disabled={!userData.mainUser}
                                />
                                <FloatingTooltip
                                    content={_t.edit.planner.blockItineraryDescription()}
                                    placement="right"
                                >
                                    <QuestionMarkCircleIcon className="w-5 h-5 cursor-pointer text-neutral-400 hover:text-neutral-600" />
                                </FloatingTooltip>
                            </div>
                            <div className="flex w-full mt-1 gap-x-1">
                                <Checkbox.Full
                                    className="flex gap-2 w-fit"
                                    label={_t.edit.planner.compactAccommodation()}
                                    {...form.registerCheckbox('compactAccommodation')}
                                />
                                <FloatingTooltip
                                    content={_t.edit.planner.compactAccommodationDescription()}
                                    placement="right"
                                >
                                    <QuestionMarkCircleIcon className="w-5 h-5 cursor-pointer text-neutral-400 hover:text-neutral-600" />
                                </FloatingTooltip>
                            </div>
                            <div className="flex w-full mt-1 gap-x-1">
                                <Checkbox.Full
                                    className="flex gap-2 w-fit"
                                    label={_t.edit.planner.compactDescriptions()}
                                    {...form.registerCheckbox('compactDescriptions')}
                                />
                                <FloatingTooltip
                                    content={_t.edit.planner.compactDescriptionsDescription()}
                                    placement="right"
                                >
                                    <QuestionMarkCircleIcon className="w-5 h-5 cursor-pointer text-neutral-400 hover:text-neutral-600" />
                                </FloatingTooltip>
                            </div>
                            <div>
                                <label htmlFor="activeProfile" className="text-xs text-neutral-700">
                                    {_t.edit.fields.activeProfile()}
                                </label>
                                <div className="flex items-center gap-x-2">
                                    <Icon icon="pepicons-pop:paint-pallet" width="1rem" height="1rem" />
                                    {itineraryOptions && (
                                        <select.Full
                                            options={itineraryOptions.map((value) => ({
                                                name: value.name,
                                                id: value.id
                                            }))}
                                            onChange={profileProps.onChange}
                                            makeReactKey={defaultMakeReactKey}
                                            accessValue={(option) => option.id}
                                            displayValue={(option, options) =>
                                                options.find((v) => v.id === option)?.name
                                            }
                                            placeholder={_t.edit.selectProfile()}
                                            className="[&>div>button]:p-0 [&>div>button]:pb-[2px] shadow-none w-full resize-none overflow-hidden text-ellipsis border-b border-gray-300 hover:border-gray-300 focus:border-gray-500 focus:outline-none [&>div]:focus-within:ring-0"
                                            value={activeProfile?.id}
                                            name={profileProps.name}
                                            noBorder={true}
                                            elementsClassName={addClasses('rounded-none')}
                                            optionsClassName={addClasses('border border-gray-100 shadow-md')}
                                        />
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="language" className="text-xs text-neutral-700">
                                    {_t.edit.fields.language()}
                                </label>
                                <div className="flex items-center mb-1 gap-x-2">
                                    <Icon icon="clarity:language-line" width="1.1rem" height="1.1rem" />
                                    {itineraryOptions && (
                                        <select.Full
                                            options={[...languages, { id: 'more', name: _t.edit.translation.more() }]}
                                            onChange={handleChangeLanguage}
                                            makeReactKey={defaultMakeReactKey}
                                            accessValue={(option) => option.id}
                                            displayValue={(option, options) => {
                                                const opt = options.find((v) => v.id === option);
                                                return opt ? (
                                                    <div className="flex justify-between w-full cursor-pointer">
                                                        <span>{opt.name}</span>
                                                        {opt.ai && (
                                                            <FloatingTooltip content={_t.edit.aiOptionTooltip()}>
                                                                <SparklesIcon
                                                                    width="1rem"
                                                                    height="1rem"
                                                                    className="mt-[2px]"
                                                                />
                                                            </FloatingTooltip>
                                                        )}
                                                    </div>
                                                ) : null;
                                            }}
                                            placeholder={_t.theme.language()}
                                            value={
                                                languages.find((el) => el.id === profileLanguage)
                                                    ? profileLanguage
                                                    : activeProfile?.language
                                            }
                                            name={languageProps.name}
                                            noBorder={true}
                                            className="[&>div>button]:p-0 [&>div>button]:pb-[2px] shadow-none resize-none overflow-hidden text-ellipsis border-b border-gray-300 hover:border-gray-300 focus:border-gray-500 focus:outline-none [&>div]:focus-within:ring-0 w-full"
                                            elementsClassName={addClasses('rounded-none')}
                                            optionsClassName={addClasses('border border-gray-100 shadow-md')}
                                            buttonClassName=""
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <SubSectionHeading>{_t.edit.contacts()}</SubSectionHeading>
                        <div className="flex flex-col gap-1 p-2 pt-1 border rounded-b bg-neutral-100 border-neutral-300">
                            <div>
                                <label htmlFor="assignedTo" className="text-xs text-neutral-700">
                                    {_t.edit.fields.assignedTo()}
                                </label>
                                <div className="flex items-center gap-x-2">
                                    <Icon icon="mdi:card-account-phone-outline" width="1rem" height="1rem" />
                                    <Combobox.Full
                                        name="assignedTo"
                                        inPortal={false}
                                        query={userQuery}
                                        onQueryChange={(query) => {
                                            if (query === '') form.handleChange('assignedTo', '');
                                            setUserQuery(query);
                                        }}
                                        onInputFocus={(e) => {
                                            setUserQuery(e.target.value);
                                        }}
                                        onChange={handleChangeAssignedTo}
                                        displayValue={(opt) => {
                                            return opt.firstName + ' ' + opt.lastName;
                                        }}
                                        placeholder={_t.edit.fields.assignedTo()}
                                        options={usersList}
                                        filterFn={() => true}
                                        nullable={true}
                                        noBorder={true}
                                        elementsClassName={addClasses('rounded-none')}
                                        inputClassName="pl-1 text-sm"
                                        className="[&>div>button]:p-0 [&>div>button]:pb-[2px] [&>div>input]:pl-0 [&>div>input]:text-md [&>div>input]:focus-within:outline-none [&>div>input]:focus-visible:outline-none [&>div>input]:focus:outline-none [&>div]:focus-within:ring-0 shadow-none w-full resize-none text-ellipsis border-b border-gray-300 hover:border-gray-300 focus:border-gray-500 focus:outline-none"
                                    ></Combobox.Full>
                                </div>
                            </div>

                            <InputText
                                value={email || ''}
                                placeholder={_t.edit.fields.email()}
                                form={form}
                                name="email"
                                icon={<Icon icon="line-md:email" width="1rem" height="1rem" />}
                            />

                            <InputText
                                value={phoneNumber || ''}
                                placeholder={_t.edit.phone()}
                                form={form}
                                name="phoneNumber"
                                icon={<Icon icon="line-md:phone" width="1rem" height="1rem" />}
                            />

                            <InputText
                                value={emergencyNumber || ''}
                                placeholder={_t.edit.fields.emergencyNumber()}
                                form={form}
                                name="emergencyNumber"
                                icon={<Icon icon="line-md:phone" width="1rem" height="1rem" />}
                            />

                            {connectionId && (
                                <InputText
                                    value={connectionId || ''}
                                    placeholder={_t.edit.connection()}
                                    form={form}
                                    readOnly={true}
                                    icon={<Icon icon="material-symbols:numbers-rounded" width="1rem" height="1rem" />}
                                />
                            )}
                        </div>
                    </div>

                    <div>
                        <SubSectionHeading>{_t.edit.otherFeatures()}</SubSectionHeading>
                        <div className="flex flex-col gap-2 p-2 border rounded-b bg-neutral-100 border-neutral-300">
                            <button
                                name="tasks"
                                className="w-full h-[40px] border border-neutral-300 rounded-lg bg-white hover:bg-neutral-100 flex justify-between items-center p-2 text-sm text-neutral-700 font-semibold"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShow(true);
                                }}
                                disabled={cantModify}
                            >
                                <Icon icon="material-symbols:groups-outline" width="1rem" height="1rem" />
                                <span className="w-full text-center">{_t.edit.clientsButton()}</span>
                            </button>
                            {diffModel && clients.some((el) => !el.notify) && (
                                <div className="w-full h-[40px] flex items-center gap-1">
                                    <Button
                                        look="primary"
                                        className={addClasses('flex-grow')}
                                        busy={isSending}
                                        onClick={handleSendNotification}
                                        disabled={cantModify}
                                    >
                                        {_t.edit.sendNotification()}
                                    </Button>
                                    <FloatingTooltip refClassName="h-full" content={_t.edit.showPendingUpdates()}>
                                        <Button
                                            className={addClasses('h-full')}
                                            onClick={handleShowPendingUpdates}
                                            disabled={!diffModel || !Object.keys(diffModel).length}
                                        >
                                            <Icon icon="octicon:file-diff-24" width="1rem" height="1rem" />
                                        </Button>
                                    </FloatingTooltip>
                                </div>
                            )}

                            <button
                                name="tasks"
                                className="w-full h-[40px] border border-neutral-300 rounded-lg bg-white hover:bg-neutral-100 flex justify-between items-center p-2 text-sm text-neutral-700 font-semibold"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowTasks(true);
                                }}
                                disabled={cantModify}
                            >
                                <div className="relative p-2">
                                    <Icon icon="mingcute:task-2-line" width="1rem" height="1rem" />
                                    {tasks && tasks.length > 0 && (
                                        <div className="absolute top-1 left-[3px] w-3 h-3 rounded-[50%] bg-primary-500 border border-white"></div>
                                    )}
                                </div>
                                <span className="w-full text-center">{_t.edit.tasksButton()}</span>
                            </button>

                            <button
                                name="feedbacks"
                                look="white"
                                className="w-full h-[40px] border border-neutral-300 rounded-lg bg-white hover:bg-neutral-100 flex justify-between items-center p-2 text-sm text-neutral-700 font-semibold"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowFeedbacks(true);
                                }}
                            >
                                <div className="relative p-2">
                                    <Icon icon="material-symbols:feedback-outline" width="1rem" height="1rem" />
                                    {feedbacks && feedbacks.length > 0 && (
                                        <div className="absolute top-1 left-[3px] w-3 h-3 rounded-[50%] bg-primary-500 border border-white"></div>
                                    )}
                                </div>
                                <span className="w-full text-center">{_t.edit.feedbacksButton()}</span>
                            </button>
                        </div>
                    </div>
                    {id && (
                        <div>
                            <SubSectionHeading>{_t.search.tags()}</SubSectionHeading>
                            <div className="flex flex-col gap-2 p-2 border rounded-b bg-neutral-100 border-neutral-300">
                                <div className="flex flex-wrap gap-2">
                                    {tags && tags.length > 0 ? (
                                        tags.map((tag) => (
                                            <ItineraryTag
                                                key={tag.id}
                                                tag={tag}
                                                remove={
                                                    cantModify
                                                        ? () => toast.error(_t.global.missingPermissions())
                                                        : deleteTag
                                                }
                                            />
                                        ))
                                    ) : (
                                        <div className="w-full text-sm text-neutral-800">
                                            {_t.search.noItineraryTags()}
                                        </div>
                                    )}
                                </div>
                                <button
                                    name="tags"
                                    look="white"
                                    className="w-full h-[40px] border border-neutral-300 rounded-lg bg-white hover:bg-neutral-100 flex justify-between items-center p-2 text-sm text-neutral-700 font-semibold"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        showAddTagPanel(true);
                                    }}
                                    disabled={cantModify}
                                >
                                    <Icon icon="material-symbols:add" width="1.2rem" height="1.2rem" className="p-2" />
                                    <span className="w-full text-center">{_t.global.add()}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {show && (
                <MailListModal
                    isOpen={show}
                    onClose={() => setShow(false)}
                    clients={clients}
                    setClients={setClients}
                    id={id}
                />
            )}
            {showPendingUpdates && (
                <PendingUpdatesModal
                    isOpen={showPendingUpdates}
                    onClose={() => setShowPendingUpdates(false)}
                    diffModel={diffModel}
                    itineraryOptions={itineraryOptions}
                    totalDocs={documents}
                />
            )}
            {showFeedbacks && (
                <FeedbackModal
                    isOpen={showFeedbacks}
                    onClose={() => setShowFeedbacks(false)}
                    feedbacks={feedbacks}
                    setFeedbacks={setFeedbacks}
                    itineraryId={id}
                />
            )}
            {showTasks && (
                <TasksModal
                    isOpen={showTasks}
                    onClose={() => {
                        // Delete Parameters from url
                        window.history.replaceState({}, '', `${window.location.pathname}`);
                        setShowTasks(false);
                    }}
                    tasks={tasks}
                    setTasks={setTasks}
                    taskName={taskName}
                    id={id}
                />
            )}
            <TranslationPanel
                isOpen={showTranslationPanel}
                onClose={handleShowTranslationPanel}
                handleClick={handleClick}
            />
            {addTagPanel && (
                <AddTagPanel isOpen={addTagPanel} onClose={() => showAddTagPanel(false)} addTag={associateTag} />
            )}
        </form>
    );
}

export function InputText(prop) {
    const { ref, name, type, value, placeholder, form, icon, className, readOnly } = prop;
    let inputType;
    if (name === 'email') {
        inputType = 'email';
    } else if (name === 'phone') {
        inputType = 'tel';
    } else {
        inputType = type ?? 'text';
    }

    return (
        <div>
            <label htmlFor={name} className="text-xs text-neutral-700">
                {placeholder}
            </label>
            <div className="flex items-center gap-x-2">
                {icon}
                <input
                    ref={ref}
                    id={name}
                    type={inputType}
                    {...form.register(name)}
                    className={mergeClasses(
                        cn(
                            'w-full pl-1 text-ellipsis border-b border-neutral-300 bg-white text-sm',
                            !readOnly && 'hover:border-neutral-300 focus:border-neutral-500 focus:outline-none'
                        ),
                        className
                    )}
                    placeholder={placeholder}
                    value={value}
                    disabled={readOnly}
                />
            </div>
        </div>
    );
}
